import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { updateBranding, setOnboarding, tenantEmbed, billingSummary, billingSubscribe, uploadArtwork, trackActivation } from "../../lib/api";
import { track } from "../../lib/analytics";
import { RequireAuth } from "./guards";
import { Shell, Card, H1, Btn, Label, Input } from "./ui";

const INDUSTRIES = [
  ["gym_flooring", "Gym flooring"],
  ["turf_installer", "Turf installer"],
  ["sports_facility", "Sports facility"],
  ["other", "Other"],
];
const STEPS = ["Welcome", "Branding", "Industry", "Your designer", "Choose a plan"];

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="onboarding-stepper">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= step ? "bg-[#00D26A] text-black" : "bg-white/10 text-white/50"}`}>{i + 1}</div>
          <span className={`text-xs ${i === step ? "text-white" : "text-white/40"}`}>{s}</span>
          {i < STEPS.length - 1 && <div className="w-6 h-px bg-white/15" />}
        </div>
      ))}
    </div>
  );
}

function Inner() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [brand, setBrand] = useState({ name: "", logo_url: "", primary: "#00D26A", secondary: "#161616", accent: "#ffffff" });
  const [industry, setIndustry] = useState("");
  const [embed, setEmbed] = useState(null);
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("yearly");
  const [busy, setBusy] = useState(false);
  const yearly = interval === "yearly";

  useEffect(() => {
    tenantEmbed().then(setEmbed).catch(() => {});
    billingSummary().then((d) => { setPlans(d.plans || []); setBrand((b) => ({ ...b, name: d.tenant?.name || b.name })); }).catch(() => {});
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const r = await uploadArtwork(file); setBrand((b) => ({ ...b, logo_url: r.url || r.file_url })); toast.success("Logo uploaded"); }
    catch { toast.error("Could not upload logo"); }
  };
  const saveBranding = async () => {
    setBusy(true);
    try { await updateBranding({ name: brand.name, logo_url: brand.logo_url, primary: brand.primary, secondary: brand.secondary, accent: brand.accent }); setStep(2); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save (owner/admin only)"); } finally { setBusy(false); }
  };
  const saveIndustry = async () => { try { await setOnboarding({ industry }); } catch {} trackActivation("designer_preview_viewed"); setStep(3); };
  const subscribe = async (plan_id) => {
    setBusy(true);
    try { const r = await billingSubscribe(plan_id, interval); if (r?.checkout_url) { track("checkout_started", { plan_id, interval }); track("trial_started", { plan_id, interval }); window.location.href = r.checkout_url; return; } toast.success("Plan updated"); nav("/dashboard"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); } finally { setBusy(false); }
  };
  const money = (n) => `£${Number(n || 0).toFixed(2)}`;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto" data-testid="onboarding-page">
        <div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-2">Setup</div>
        <Stepper step={step} />

        {step === 0 && (
          <Card data-testid="onboarding-welcome">
            <H1>Welcome — let's set up your designer</H1>
            <p className="text-white/60 mt-3 text-sm leading-relaxed">In the next 3 quick steps you'll add your branding, tell us your industry and get a ready-to-embed AI turf designer. Then start your 14-day free trial. This takes about 5 minutes.</p>
            <Btn onClick={() => setStep(1)} data-testid="onboarding-start" className="mt-6">Let's go →</Btn>
          </Card>
        )}

        {step === 1 && (
          <Card data-testid="onboarding-branding">
            <h2 className="text-xl font-semibold mb-4">Add your branding</h2>
            <div className="grid gap-4">
              <div><Label>Company name</Label><Input value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} data-testid="ob-company" /></div>
              <div>
                <Label>Logo</Label>
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={uploadLogo} data-testid="ob-logo-upload" className="text-xs text-white/70" />
                  {brand.logo_url && <img src={brand.logo_url} alt="logo" className="h-10 rounded border border-white/10" />}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {["primary", "secondary", "accent"].map((k) => (
                  <div key={k}><Label>{k}</Label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={brand[k]} onChange={(e) => setBrand({ ...brand, [k]: e.target.value })} data-testid={`ob-color-${k}`} className="w-10 h-9 bg-transparent rounded" />
                      <Input value={brand[k]} onChange={(e) => setBrand({ ...brand, [k]: e.target.value })} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-6"><Btn variant="ghost" onClick={() => setStep(0)}>Back</Btn><Btn onClick={saveBranding} disabled={busy} data-testid="ob-branding-next">Continue</Btn></div>
          </Card>
        )}

        {step === 2 && (
          <Card data-testid="onboarding-industry">
            <h2 className="text-xl font-semibold mb-4">What best describes your business?</h2>
            <div className="grid grid-cols-2 gap-3">
              {INDUSTRIES.map(([k, label]) => (
                <button key={k} onClick={() => setIndustry(k)} data-testid={`ob-industry-${k}`} className={`text-left p-4 rounded border ${industry === k ? "border-[#00D26A] bg-[#00D26A]/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="font-semibold">{label}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-6"><Btn variant="ghost" onClick={() => setStep(1)}>Back</Btn><Btn onClick={saveIndustry} disabled={!industry} data-testid="ob-industry-next">Continue</Btn></div>
          </Card>
        )}

        {step === 3 && (
          <Card data-testid="onboarding-designer-ready">
            <h2 className="text-xl font-semibold mb-1">Your designer is ready 🎉</h2>
            <p className="text-white/60 text-sm mb-4">Here's your public designer and the embed code for your website. It goes fully live (and starts capturing leads) once your plan or trial is active.</p>
            <Label>Public designer URL</Label>
            <div className="flex gap-2 mb-3"><Input value={embed?.embed_url || ""} readOnly data-testid="ob-designer-url" /><Btn variant="ghost" className="text-xs whitespace-nowrap" onClick={() => { navigator.clipboard.writeText(embed?.embed_url || ""); toast.success("Copied"); }}>Copy</Btn></div>
            <Label>Embed code</Label>
            <pre className="bg-[#0a0a0a] border border-white/10 rounded p-3 text-xs text-white/70 overflow-x-auto whitespace-pre-wrap" data-testid="ob-embed-snippet">{embed?.iframe_snippet || ""}</pre>
            <div className="flex gap-2 mt-4">
              <Btn variant="ghost" onClick={() => setStep(2)}>Back</Btn>
              {embed?.embed_url && <a href={embed.embed_url} target="_blank" rel="noreferrer" data-testid="ob-test-designer"><Btn variant="ghost">Test my designer ↗</Btn></a>}
              <Btn onClick={() => setStep(4)} data-testid="ob-to-plans">Choose a plan →</Btn>
            </div>
          </Card>
        )}

        {step === 4 && (
          <div data-testid="onboarding-plans">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Start your 14-day free trial</h2>
              <div className="flex items-center gap-1 bg-[#141414] border border-white/15 rounded-full p-1">
                <button onClick={() => setInterval("monthly")} data-testid="ob-interval-monthly" className={`px-3 py-1 text-xs rounded-full ${!yearly ? "bg-[#00D26A] text-black font-semibold" : "text-white/60"}`}>Monthly</button>
                <button onClick={() => setInterval("yearly")} data-testid="ob-interval-yearly" className={`px-3 py-1 text-xs rounded-full ${yearly ? "bg-[#00D26A] text-black font-semibold" : "text-white/60"}`}>Yearly · Save ~17%</button>
              </div>
            </div>
            <p className="text-white/50 text-sm mb-4">No charge today — we'll only bill when your trial ends. Cancel anytime.</p>
            <div className="grid md:grid-cols-4 gap-4">
              {plans.map((p) => (
                <Card key={p.plan_id} className={p.highlight ? "border-[#00D26A]/50" : ""}>
                  {p.highlight && <div className="text-[10px] text-[#00D26A] font-bold uppercase mb-1">Most popular</div>}
                  <div className="font-semibold">{p.name}</div>
                  {yearly ? (
                    <><div className="text-2xl font-bold mt-1">{money(p.price_gbp_yearly)}<span className="text-xs text-white/40 font-normal">/yr</span></div>
                    <div className="text-[11px] text-white/40">≈ {money((p.price_gbp_yearly || 0) / 12)}/mo</div></>
                  ) : (<div className="text-2xl font-bold mt-1">{money(p.price_gbp)}<span className="text-xs text-white/40 font-normal">/mo</span></div>)}
                  <div className="text-xs text-[#00D26A] mt-1">{p.ai_allowance} AI generations/mo</div>
                  <ul className="text-xs text-white/50 mt-3 space-y-1 min-h-[76px]">{(p.features || []).map((f, i) => <li key={i}>• {f}</li>)}</ul>
                  <Btn onClick={() => subscribe(p.plan_id)} disabled={busy} data-testid={`ob-plan-${p.plan_id}`} className="w-full mt-3 text-xs">Start free trial</Btn>
                </Card>
              ))}
            </div>
            <button onClick={() => nav("/dashboard")} className="text-white/40 text-xs mt-5 hover:text-white" data-testid="ob-skip-plan">Skip for now — go to dashboard</button>
          </div>
        )}
      </div>
    </Shell>
  );
}

export default function Onboarding() { return <RequireAuth><Inner /></RequireAuth>; }
